import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";
import { describeCause } from "@/lib/reportError";

/**
 * One-shot auto-title generation for a brand-new conversation. Issue #1252.
 *
 * Extracted from Index.tsx so the failure handling is testable — the bug this
 * exists to survive is not reproducible from source, so the behaviour around it
 * needs to be pinned rather than eyeballed.
 *
 * ── What went wrong ────────────────────────────────────────────────────────
 *
 * Sentry JAVASCRIPT-REACT-17: `SyntaxError: Unexpected token 'd', "data: {"ch"…`
 * on a new conversation. The title fetch got an SSE stream where it asked for
 * JSON, so `resp.json()` threw and the chat got no title.
 *
 * The issue's original reading was that the request fell through kin-chat's
 * `title_only` early branch into the main chat path. **That is impossible**,
 * and ruling it out is what makes the fix below the right shape:
 *
 *   - The first statement after `END EARLY BRANCHES` is a guard on `messages`
 *     (kin-chat/index.ts:2981) returning **400 JSON**. This caller sends no
 *     `messages`, so a fall-through is caught by `!resp.ok` and never reaches
 *     `resp.json()`. It cannot produce a SyntaxError.
 *   - `increment_message_count` (index.ts:3376/:3602) sits ~400 lines AFTER
 *     that guard, so a fall-through burns no message either.
 *   - The payload shape `data: {"choices":[{"delta":…` is emitted only by
 *     `callOpenAIStreaming` (index.ts:2123-2255), which has exactly ONE caller
 *     — index.ts:3365, reached only after Anthropic errors on a turn that
 *     already passed the `messages` guard.
 *
 * So the response belonged to a **different request**: the main chat turn in
 * flight moments earlier to the same URL, which happened to fail over to
 * OpenAI. Transport-level, intermittent, and not reachable from kin-chat's
 * source — which is why this retries rather than trying to prevent it.
 *
 * ⚠️ It deliberately does NOT parse the stream. Reading a title out of an SSE
 * body would build titles from full chat turns and hide the mismatch entirely.
 * A wrong-shaped response is reported and retried, never salvaged.
 */

const CHAT_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-chat`;

export type TitleFailure =
  | "no-session"
  | "http-error"
  | "wrong-content-type"
  | "empty-title"
  | "threw";

export interface TitleResult {
  ok: boolean;
  title?: string;
  failure?: TitleFailure;
  /** Carried into the log line so a repeat is diagnosable without a redeploy. */
  detail?: string;
}

/** True when the body is safe to hand to `.json()`. */
export function isJsonResponse(contentType: string | null): boolean {
  return (contentType ?? "").toLowerCase().includes("application/json");
}

async function attempt(
  accessToken: string,
  firstUserMessage: string,
  firstAssistantMessage: string,
  fetchImpl: typeof fetch
): Promise<TitleResult> {
  const resp = await fetchImpl(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      // #1257 — the other 12 kin-chat call sites all send this; this one did
      // not. Not the cause of #1252 (a missing apikey would 401, not stream),
      // but the callers should agree.
      apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({
      action: "title_only",
      first_user_message: firstUserMessage,
      first_assistant_message: firstAssistantMessage,
    }),
  });

  if (!resp.ok) {
    return { ok: false, failure: "http-error", detail: String(resp.status) };
  }

  // Check the shape BEFORE parsing. Every `title_only` exit path in kin-chat
  // sets `Content-Type: application/json` — empty-input guard, missing-key
  // guard, Anthropic error, OpenAI fallback, success and its catch — so a
  // non-JSON body here means this is not our response at all. Parsing it
  // blind is what produced a SyntaxError that read like a kin-chat bug.
  const contentType = resp.headers.get("content-type");
  if (!isJsonResponse(contentType)) {
    return { ok: false, failure: "wrong-content-type", detail: contentType ?? "(none)" };
  }

  const data = await resp.json();
  const title = (data?.title ?? "").toString().trim();
  if (!title) return { ok: false, failure: "empty-title" };
  return { ok: true, title };
}

/**
 * A thrown value, as a string.
 *
 * Delegates to `describeCause` rather than `String(err)`, which turns a plain
 * rejection like `{ code: "ECONNRESET" }` into a literal "[object Object]" —
 * the same unreadable title #1273 exists to repair, reintroduced one layer
 * lower where a beforeSend cannot see it. One flattener for the whole app.
 *
 * ⚠️ Known limit, pinned in the tests rather than papered over: `describeCause`
 * falls back to a key list when an object has a `code` but no `message`, so
 * `{ code: "ECONNRESET" }` reads as "non-error object with keys: code". Ugly,
 * but greppable and honest — and unlike "[object Object]" it still groups.
 */
function describeThrown(err: unknown): string {
  return describeCause(err);
}

/**
 * Asks kin-chat for a title, retrying ONCE if the response is not JSON.
 *
 * The retry is the actual fix: title generation is idempotent — one Haiku call
 * and one `conversations.update` — so re-asking is cheap and safe, and a
 * response mismatch is transient by nature. Only `wrong-content-type` retries;
 * an HTTP error or an empty title is a real answer and re-asking would not
 * change it.
 *
 * ⚠️ The two attempts do NOT share a `try`. Issue #1278: they used to, so a
 * throw on the SECOND attempt landed in the outer catch, which has no
 * reference to `first` and reported a bare network message. That dropped the
 * `wrong-content-type` which is the whole signature of #1252 — in the one
 * path where having both facts is most informative, because a shape mismatch
 * followed immediately by a throw says more about transport-level crossing
 * than either symptom alone.
 *
 * It matters beyond this function: `Index.tsx` logs `failure` and `detail` via
 * `console.error`, and `captureConsoleIntegration` makes that the TITLE of the
 * Sentry event. A dropped detail does not just lose context, it points triage
 * at a generic network failure instead of at #1252.
 */
export async function requestChatTitle(
  accessToken: string | undefined,
  firstUserMessage: string,
  firstAssistantMessage: string,
  fetchImpl: typeof fetch = fetch
): Promise<TitleResult> {
  if (!accessToken) return { ok: false, failure: "no-session" };

  try {
    const first = await attempt(accessToken, firstUserMessage, firstAssistantMessage, fetchImpl);
    if (first.ok || first.failure !== "wrong-content-type") return first;

    // The retry gets its own try/catch so that BOTH facts survive a throw
    // here. `failure` stays "threw" — nothing downstream that switches on it
    // changes behaviour; only `detail` gains the part that was being lost.
    let second: TitleResult;
    try {
      second = await attempt(accessToken, firstUserMessage, firstAssistantMessage, fetchImpl);
    } catch (err) {
      return {
        ok: false,
        failure: "threw",
        detail: `retried after ${first.detail}; second attempt threw: ${describeThrown(err)}`,
      };
    }

    if (second.ok) return second;
    // Report the FIRST content-type — that is the evidence about the mismatch.
    // A silent retry that swallows what it saw is how this stayed unexplained.
    return {
      ...second,
      detail: `retried after ${first.detail}; second attempt: ${second.detail ?? second.failure}`,
    };
  } catch (err) {
    // Reached only by a throw on the FIRST attempt now. Bare message, no
    // invented retry prefix — there was no retry.
    return { ok: false, failure: "threw", detail: describeThrown(err) };
  }
}
